export const COUPON_STATUSES = ["ALL", "SCHEDULED", "ACTIVE", "EXPIRED", "DISABLED", "UNAVAILABLE"];

// LocalDateTime has no offset. Compare normalized wall-clock strings, retaining nanosecond precision.
export function couponTimestamp(value) {
    if (typeof value !== "string") return null;
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?$/);
    if (!match) return null;
    const [, year, month, day, hour, minute, second = "00", fraction = ""] = match;
    const leap = Number(year) % 4 === 0 && (Number(year) % 100 !== 0 || Number(year) % 400 === 0);
    const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (+month < 1 || +month > 12 || +day < 1 || +day > days[+month - 1] || +hour > 23 || +minute > 59 || +second > 59) return null;
    return `${year}-${month}-${day}T${hour}:${minute}:${second}.${fraction.padEnd(9, "0")}`;
}

export function couponLocalNow(date = new Date()) {
    const pad = (value, length = 2) => String(value).padStart(length, "0");
    return `${pad(date.getFullYear(), 4)}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${pad(date.getMilliseconds(), 3)}000000`;
}

export function couponLifecycle(coupon, now = couponLocalNow()) {
    if (coupon.active === false) return "DISABLED";
    const start = couponTimestamp(coupon.startDate), end = coupon.endDate == null ? null : couponTimestamp(coupon.endDate);
    const current = couponTimestamp(now);
    if (coupon.active !== true || !current || (coupon.startDate != null && !start) ||
        (coupon.endDate != null && (!end || (start && end < start)))) return "UNAVAILABLE";
    if (end && current > end) return "EXPIRED";
    if (start && current < start) return "SCHEDULED";
    return "ACTIVE";
}

export function couponClockDelay(coupons, now) {
    const milliseconds = value => Date.parse(`${value.slice(0, 23)}Z`);
    const current = milliseconds(now);
    const boundaries = coupons.flatMap(coupon => [
        [couponTimestamp(coupon.startDate), false], [couponTimestamp(coupon.endDate), true],
    ]).filter(([value]) => value).map(([value, end]) =>
        milliseconds(value) + (end || /[1-9]/.test(value.slice(23)) ? 1 : 0) - current).filter(delay => delay > 0);
    return Math.min(60000, ...boundaries);
}

export function formatCouponDate(value, language, unavailable) {
    const timestamp = couponTimestamp(value);
    if (!timestamp) return unavailable;
    // UTC is only a formatter here: do not shift the stored local date/time to another zone.
    return new Intl.DateTimeFormat(language || "en", {year: "numeric", month: "short", day: "numeric",
        hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "UTC"}).format(new Date(`${timestamp.slice(0, 23)}Z`));
}

export const formatCouponDiscount = (value, language, unavailable) => typeof value === "number" && Number.isFinite(value) && value > 0 && value <= 100
    ? new Intl.NumberFormat(language || "en", {style: "percent", maximumFractionDigits: 20}).format(value / 100) : unavailable;

export function filterCoupons(coupons, search, status, now) {
    const query = search.trim().toLocaleLowerCase();
    return coupons.filter(coupon => String(coupon.couponCode || "").toLocaleLowerCase().includes(query) &&
        (status === "ALL" || couponLifecycle(coupon, now) === status));
}
